import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Message } from './Message';
import { AvatarProviderToggle } from './AvatarProviderToggle';
import type { Message as MessageType, DynamicCase } from '../../types';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import './Chat.css';
import { Box, IconButton, TextField } from '@mui/material';
import type { ExchangeTimingMetadata } from '../../services/exchangeTiming';

interface ChatContainerProps {
  messages: MessageType[];
  onSendMessage: (message: string, fromSpeech?: boolean, timing?: ExchangeTimingMetadata) => void;
  onSubmitDiagnosis: (diagnosis: string) => void;
  isProcessing: boolean;
  currentCase: DynamicCase | null;
  variant?: 'default' | 'conversationOverlay';
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  onSendMessage,
  onSubmitDiagnosis: _onSubmitDiagnosis,
  isProcessing,
  currentCase,
  variant = 'default'
}) => {
  const [questionInput, setQuestionInput] = useState('');
  const inputStartedAtRef = useRef<number | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isConversationOverlay = variant === 'conversationOverlay';
  const visibleMessages = messages;
  
  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [visibleMessages]);
  
  // Handle question submission
  const handleSendQuestion = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedQuestion = questionInput.trim();
    if (!trimmedQuestion || !currentCase || isProcessing) {
      return;
    }
    
    const inputCompletedAt = performance.now();
    onSendMessage(trimmedQuestion, false, {
      inputMode: 'typed',
      inputStartedAt: inputStartedAtRef.current ?? inputCompletedAt,
      inputCompletedAt,
    });
    setQuestionInput('');
    inputStartedAtRef.current = null;
  }, [questionInput, currentCase, isProcessing, onSendMessage]);
  
  // Handle Enter key
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuestion(e as any);
    }
  }, [handleSendQuestion]);
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: '20px',
        color: 'text.primary',
        backgroundColor: 'transparent',
        minHeight: 0,
      }}
    >
      <AvatarProviderToggle />
      {/* Messages Area */}
      <Box
        id="chatContainer"
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: isConversationOverlay ? '0 0 14px' : '4px 6px 18px',
          minHeight: 0,
          color: 'text.primary',
          background: 'transparent',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '999px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(15, 23, 42, 0.14)',
            borderRadius: '999px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(15, 23, 42, 0.24)'
          }
        }}
      >
        {visibleMessages.length === 0 && !isConversationOverlay && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              padding: '10px 6px'
            }}
          >
            <div className="message system">
              {currentCase 
                ? `Case loaded: ${currentCase.title}. You may begin your diagnostic interview.`
                : 'Select a case to begin your diagnostic interview.'
              }
            </div>
          </Box>
        )}
        
        {visibleMessages.map((message, index) => (
          <Message key={message.id || index} message={message} variant={variant} />
        ))}
      </Box>
      
      {/* Question Input */}
      <Box
      sx={{
        pt: isConversationOverlay ? 0.5 : 1.5,
        px: isConversationOverlay ? 0 : 1.5,
        pb: isConversationOverlay ? 0 : 1,
        borderTop: isConversationOverlay ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
        backgroundColor: isConversationOverlay ? 'transparent' : 'rgba(255,255,255,0.86)',
        borderBottomLeftRadius: '18px',
        borderBottomRightRadius: '18px',
        backdropFilter: isConversationOverlay ? 'none' : 'blur(8px)',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          minHeight: isConversationOverlay ? 54 : 'auto',
          gap: isConversationOverlay ? 0.5 : 1,
          alignItems: 'center',
          border: isConversationOverlay ? '1px solid rgba(15, 23, 42, 0.14)' : 'none',
          borderRadius: isConversationOverlay ? '8px' : 0,
          px: isConversationOverlay ? 1.75 : 0,
          py: isConversationOverlay ? 0.6 : 0,
          backgroundColor: isConversationOverlay ? '#ffffff' : 'transparent',
        }}
      >
        <TextField
          value={questionInput}
          ref={inputRef}
          onChange={(e) => {
            if (inputStartedAtRef.current === null && e.target.value.length > 0) {
              inputStartedAtRef.current = performance.now();
            }
            if (e.target.value.length === 0) {
              inputStartedAtRef.current = null;
            }
            setQuestionInput(e.target.value);
          }}
          onKeyPress={handleKeyPress}
          placeholder="Type a response"
          fullWidth
          size="small"
          variant={isConversationOverlay ? 'standard' : 'outlined'}
          sx={{
            ...(isConversationOverlay
              ? {
                  '& .MuiInputBase-root': {
                    minHeight: 42,
                    '&:before': { display: 'none' },
                    '&:after': { display: 'none' },
                  },
                }
              : {}),
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255,255,255,0.85)',
              color: '#111827',
              borderRadius: '16px',
              '& fieldset': {
                borderColor: 'rgba(15, 23, 42, 0.08)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(15, 23, 42, 0.16)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3399A3',
              },
            },
            '& .MuiInputBase-input': {
              color: '#111827',
              fontStyle: 'italic',
              fontSize: isConversationOverlay ? '0.98rem' : undefined,
              '&::placeholder': {
                color: '#6b7280',
                opacity: 1,
              },
            },
          }}
        />
        <IconButton 
          onClick={handleSendQuestion}
          size="small"
          sx={{
            width: isConversationOverlay ? 36 : 42,
            height: isConversationOverlay ? 36 : 42,
            backgroundColor: isConversationOverlay ? 'transparent' : '#3399A3',
            color: isConversationOverlay ? '#374151' : 'white',
            '&:hover': {
              backgroundColor: isConversationOverlay ? 'rgba(15, 23, 42, 0.04)' : '#2d8c95',
            },
            '& svg': {
              fontSize: isConversationOverlay ? 24 : 20,
            },
            p: 0,
          }}
        >
          <SendOutlinedIcon />
        </IconButton>
      </Box>
          </Box>
    </Box>
  );
};
